import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { voidReceipt } from '../../api/cheques';
import { useAuth } from '../../context/AuthContext';

const statusColor = { DRAFT: 'badge-yellow', APPROVED: 'badge-blue', IN_HAND: 'badge-yellow', BANKED: 'badge-green', RETURNED: 'badge-red', VOID: 'badge-red' };

export default function ReceiptVoucherList() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('receipt_vouchers')
      .select('*, currencies(code), deposited_into:received_into_account_id(account_name), income:income_account_id(account_name)')
      .order('created_at', { ascending: false });
    if (q) query = query.or(`voucher_number.ilike.%${q}%,received_from.ilike.%${q}%`);
    if (dateFrom) query = query.gte('voucher_date', dateFrom);
    if (dateTo) query = query.lte('voucher_date', dateTo);
    const { data } = await query;
    setVouchers(data || []);
    setLoading(false);
  }, [q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleVoid = async () => {
    if (!voidTarget) return;
    try {
      // Full undo: receipt journal, any bank deposit, invoice allocations, and any deposit
      // already applied to an invoice (moved back onto AR).
      const { reopened, movedToAr } = await voidReceipt(voidTarget, { reason: voidReason, username });
      if (movedToAr > 0 || reopened.length) {
        alert(`${voidTarget.voucher_number} voided.${movedToAr > 0 ? `\nLKR ${movedToAr.toLocaleString()} of this deposit had already been applied to invoices — it is back on the client's balance due.` : ''}${reopened.length ? `\nReopened: ${reopened.join(', ')}` : ''}`);
      }
      setVoidTarget(null); setVoidReason(''); load();
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Receipt Vouchers</div><div className="page-subtitle">All incoming payments received</div></div>
        <Link to="/vouchers/receipt/new" className="btn btn-primary">+ New Receipt</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <input className="form-input" placeholder="Search voucher number or payer..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            <span>Date</span>
            <input type="date" className="form-input" style={{ width: 150 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
            <span>to</span>
            <input type="date" className="form-input" style={{ width: 150 }} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
            {(dateFrom || dateTo) && (
              <button className="btn btn-sm btn-secondary" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear dates</button>
            )}
          </div>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{vouchers.length} voucher{vouchers.length === 1 ? '' : 's'}</span>
        </div>
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Voucher No</th><th>Date</th><th>Received From</th><th>Bank Account</th><th>Income Account</th><th style={{ textAlign: 'right' }}>Amount</th><th>Currency</th><th>Mode</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.id} style={{ opacity: ['VOID', 'RETURNED'].includes(v.voucher_status) ? 0.5 : 1 }}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}><Link to={`/vouchers/receipt/${v.id}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{v.voucher_number}</Link></td>
                    <td>{v.voucher_date}</td>
                    <td>{v.received_from || ''}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v.deposited_into?.account_name || ''}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v.income?.account_name || ''}</td>
                    <td style={{ textAlign: 'right' }} className="amount">{parseFloat(v.amount || 0).toLocaleString()}</td>
                    <td><span className="badge badge-green">{v.currencies?.code || ''}</span></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{v.payment_mode || ''}</td>
                    <td><span className={`badge ${statusColor[v.voucher_status] || 'badge-gray'}`}>{v.voucher_status}</span></td>
                    <td>
                      {!['VOID', 'RETURNED'].includes(v.voucher_status) && (
                        <button className="btn btn-secondary btn-sm" onClick={() => { setVoidTarget(v); setVoidReason(''); }}>Void</button>
                      )}
                    </td>
                  </tr>
                ))}
                {vouchers.length === 0 && <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-title">{q || dateFrom || dateTo ? 'No receipt vouchers match your search' : 'No receipt vouchers yet'}</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {voidTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 420, padding: 24 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>Void Receipt Voucher</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Voiding <strong>{voidTarget.voucher_number}</strong> will create a reversal journal entry. This cannot be undone.
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Void Reason *</label>
              <input className="form-input" value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Reason for voiding..." />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setVoidTarget(null)}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }} onClick={handleVoid} disabled={!voidReason}>Confirm Void</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
